import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const TYPES = [["phone", "Phone"], ["office", "In-office"], ["home", "In-home"]];
const STATUSES = [["scheduled", "Scheduled"], ["completed", "Completed"], ["canceled", "Canceled"], ["no_show", "No-show"]];
const blank = { name: "", phone: "", email: "", type: "phone", starts_at: "", address: "", notes: "", status: "scheduled" };
const local = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const Pick = ({ value, options, onChange, testId }) => (
  <div className="mt-1 flex flex-wrap gap-1" data-testid={testId}>
    {options.map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
        className={`rounded-lg border px-3 py-1 text-sm font-semibold transition-colors ${value === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>{l}</button>
    ))}
  </div>
);

export const PmApptDialog = ({ open, onClose, appt, onSaved }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(appt ? { ...blank, ...appt, starts_at: local(appt.starts_at) } : blank); }, [appt, open]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const save = async () => {
    setBusy(true);
    const body = { ...f, starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : "" };
    try {
      if (appt) await api.put(`/pm/appointments/${appt.id}`, body);
      else await api.post("/pm/appointments", body);
      toast.success(appt ? "Appointment updated" : "Appointment added"); onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="pm-appt-dialog">
        <DialogHeader><DialogTitle>{appt ? "Edit appointment" : "Add appointment"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {[["name", "Name"], ["phone", "Phone"], ["email", "Email"]].map(([k, l]) => (
            <div key={k}><Label>{l}</Label><Input value={f[k]} onChange={(e) => set(k, e.target.value)} className="mt-1" data-testid={`pm-appt-${k}`} /></div>
          ))}
          <div><Label>Date &amp; time</Label><Input type="datetime-local" value={f.starts_at} onChange={(e) => set("starts_at", e.target.value)} className="mt-1" data-testid="pm-appt-starts" /></div>
          <div className="sm:col-span-2"><Label>Type</Label><Pick value={f.type} options={TYPES} onChange={(v) => set("type", v)} testId="pm-appt-type" /></div>
          <div className="sm:col-span-2"><Label>Address {f.type === "office" ? "(blank = our office)" : ""}</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} className="mt-1" data-testid="pm-appt-address" /></div>
          <div className="sm:col-span-2"><Label>Notes</Label><Input value={f.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" data-testid="pm-appt-notes" /></div>
          {appt && <div className="sm:col-span-2"><Label>Status</Label><Pick value={f.status} options={STATUSES} onChange={(v) => set("status", v)} testId="pm-appt-status" /></div>}
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose} data-testid="pm-appt-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !f.name.trim() || !f.starts_at} data-testid="pm-appt-save">{busy ? "Saving..." : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
